import { useEffect, useMemo, useState } from "react";

import { fetchMovieHalls } from "../api";
import { clockOf, formatDuration } from "../format";
import { fromCompact } from "../plan";
import type { Movie, MovieHall, Showtime } from "../types";
import { Dialog } from "./Dialog";
import { SeatPlan } from "./SeatPlan";

type Props = {
  cinemaSlug: string;
  date: string;
  movie: Movie;
  onOpenShowtime: (showtime: Showtime) => void;
  onClose: () => void;
};

const FREE_FILTERS = [0, 50, 100, 200];

export function FilmDialog({ cinemaSlug, date, movie, onOpenShowtime, onClose }: Props) {
  const [halls, setHalls] = useState<MovieHall[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [minFree, setMinFree] = useState(0);
  const [sort, setSort] = useState<"time" | "empty">("time");

  useEffect(() => {
    let cancelled = false;
    setHalls(null);
    setError(null);

    fetchMovieHalls(cinemaSlug, movie.slug, date)
      .then((result) => !cancelled && setHalls(result.showtimes))
      .catch((cause: Error) => !cancelled && setError(cause.message));

    return () => {
      cancelled = true;
    };
  }, [cinemaSlug, movie.slug, date]);

  const shown = useMemo(() => {
    const list = (halls ?? []).filter((hall) => (hall.free ?? 0) >= minFree || !hall.available);
    return sort === "empty"
      ? [...list].sort((a, b) => (b.free ?? -1) - (a.free ?? -1))
      : list;
  }, [halls, minFree, sort]);

  return (
    <Dialog
      title={movie.title}
      subtitle={`${formatDuration(movie.duration)} · ${movie.genres.slice(0, 2).join(", ")}`}
      width="wide"
      onClose={onClose}
    >
      <div className="filters">
        <span className="filters-label">Places libres</span>
        {FREE_FILTERS.map((threshold) => (
          <button
            key={threshold}
            type="button"
            className={threshold === minFree ? "pill is-on" : "pill"}
            onClick={() => setMinFree(threshold)}
          >
            {threshold === 0 ? "Toutes" : `≥ ${threshold}`}
          </button>
        ))}
        <span className="filters-label" style={{ marginLeft: 12 }}>
          Trier
        </span>
        <button
          type="button"
          className={sort === "time" ? "pill is-on" : "pill"}
          onClick={() => setSort("time")}
        >
          Par horaire
        </button>
        <button
          type="button"
          className={sort === "empty" ? "pill is-on" : "pill"}
          onClick={() => setSort("empty")}
        >
          Plus vides d'abord
        </button>
        {halls && (
          <span className="state" style={{ marginLeft: "auto" }}>
            {shown.length} / {halls.length} séances
          </span>
        )}
      </div>

      {!halls && !error && (
        <div className="loading">
          <div className="spinner" />
          <span className="state">lecture des {movie.showtimes.length} plans de salle…</span>
        </div>
      )}

      {error && <p className="state is-error">{error}</p>}

      {halls && shown.length === 0 && (
        <div className="empty">
          <p className="empty-title">Aucune séance à ce seuil</p>
          <p className="empty-text">Essayez un nombre de places libres plus bas.</p>
        </div>
      )}

      {halls && shown.length > 0 && (
        <div className="hall-cards">
          {shown.map((hall) => (
            <button
              type="button"
              className="hall-card"
              key={hall.showtime.bookingUrl}
              onClick={() => onOpenShowtime(hall.showtime)}
            >
              <span className="hall-card-top">
                <span className="hall-card-time">{clockOf(hall.showtime.time)}</span>
                <span className="tag-version">{hall.showtime.version}</span>
                <span className="hall-card-fill">
                  {hall.available
                    ? `${Math.round((hall.occupancy ?? 0) * 100)} % · ${hall.free} libres`
                    : "plan indisponible"}
                </span>
              </span>

              <span className="film-meta" style={{ margin: 0 }}>
                <span>
                  Salle {hall.showtime.auditorium} · {hall.showtime.capacity} pl.
                </span>
                {hall.showtime.formats.map((format) => (
                  <span className="tag-format" key={format}>
                    {format}
                  </span>
                ))}
                {hall.showtime.soldOut && <span className="tag-full">COMPLET</span>}
              </span>

              <span className="bar">
                <span
                  className="bar-fill"
                  style={{ width: `${Math.round((hall.occupancy ?? 0) * 100)}%` }}
                />
              </span>

              {hall.plan && <SeatPlan plan={fromCompact(hall.plan)} variant="mini" />}
            </button>
          ))}
        </div>
      )}
    </Dialog>
  );
}
